import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle, RefreshCw } from 'lucide-react'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Form } from '@/components/ui/form'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Separator } from '@/components/ui/separator'

import type { Campaign } from '@/lib/api'
import { useCreateCampaign, useUpdateCampaign } from '@/hooks/use-mutations'

import { campaignSchema, type CampaignFormInput, type CampaignFormOutput } from './schema'
import { CampaignBasicInfo } from './campaign-basic-info'
import { CampaignRewards } from './campaign-rewards'
import { CampaignIntegrations } from './campaign-integrations'

interface CampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaign?: Campaign
}

export function CampaignDialog({ open, onOpenChange, campaign }: CampaignDialogProps) {
  const isEditing = !!campaign
  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign()
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign()

  const form = useForm<CampaignFormInput>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      name: '',
      description: '',
      rewardType: 'percentage',
      rewardValue: 0,
      warrantyDays: 7,
      minOrderValue: undefined,
      maxRewardValue: undefined,
      autoRefundFraud: false,
      maxSubscriptionCycles: undefined,
      yampiOncePerCustomer: true,
      yampiAccumulate: false,
      yampiIgnorePromotionProducts: false,
      isActive: true,
    },
  })

  const [showSyncAlert, setShowSyncAlert] = useState(false)
  const [pendingData, setPendingData] = useState<CampaignFormOutput | null>(null)

  // Reset form when opening/closing or changing campaign
  useEffect(() => {
    if (open) {
      if (campaign) {
        form.reset({
          name: campaign.name,
          description: campaign.description || '',
          rewardType: campaign.rewardType,
          rewardValue: campaign.rewardValue,
          warrantyDays: campaign.warrantyDays,
          minOrderValue: campaign.minOrderValue,
          maxRewardValue: campaign.maxRewardValue,
          autoRefundFraud: campaign.autoRefundFraud,
          maxSubscriptionCycles: campaign.platformConfig?.guru?.maxSubscriptionCycles,
          yampiOncePerCustomer: campaign.platformConfig?.yampi?.oncePerCustomer ?? true,
          yampiAccumulate: campaign.platformConfig?.yampi?.accumulate ?? false,
          yampiIgnorePromotionProducts:
            campaign.platformConfig?.yampi?.ignorePromotionProducts ?? false,
          isActive: campaign.isActive,
        })
      } else {
        form.reset({
          name: '',
          description: '',
          rewardType: 'percentage',
          rewardValue: 0,
          warrantyDays: 7,
          minOrderValue: undefined,
          maxRewardValue: undefined,
          autoRefundFraud: false,
          maxSubscriptionCycles: undefined,
          yampiOncePerCustomer: true,
          yampiAccumulate: false,
          yampiIgnorePromotionProducts: false,
          isActive: true,
        })
      }
    }
  }, [campaign, form, open])

  // Final execution of the mutation
  function executeMutation(validData: CampaignFormOutput) {
    // Transform flat form data to nested API structure
    const apiData = {
      name: validData.name,
      description: validData.description,
      rewardType: validData.rewardType,
      rewardValue: validData.rewardValue,
      warrantyDays: validData.warrantyDays,
      minOrderValue: validData.minOrderValue,
      maxRewardValue: validData.maxRewardValue,
      autoRefundFraud: validData.autoRefundFraud,
      isActive: validData.isActive,
      // Create platformConfig only if needed
      platformConfig: {
        guru: validData.maxSubscriptionCycles
          ? {
              maxSubscriptionCycles: validData.maxSubscriptionCycles,
            }
          : undefined,
        yampi: {
          oncePerCustomer: validData.yampiOncePerCustomer,
          accumulate: validData.yampiAccumulate,
          ignorePromotionProducts: validData.yampiIgnorePromotionProducts,
        },
      },
    }

    if (isEditing && campaign) {
      updateCampaign(
        { id: campaign.id, data: apiData },
        {
          onSuccess: () => {
            onOpenChange(false)
            setShowSyncAlert(false)
          },
        }
      )
    } else {
      createCampaign(apiData, {
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  // Hook Form handles the input type, but on submit we get the output type (numbers)
  function onSubmit(data: unknown) {
    // Cast to Output type because zod resolver ensures it matches
    const validData = data as CampaignFormOutput

    if (isEditing && campaign) {
      // Check for sync-triggering changes
      // Rules: Reward Type, Reward Value, Platform Configs
      const currentGuruCycles = validData.maxSubscriptionCycles
      const originalGuruCycles = campaign.platformConfig?.guru?.maxSubscriptionCycles

      const currentYampiOnce = validData.yampiOncePerCustomer
      const originalYampiOnce = campaign.platformConfig?.yampi?.oncePerCustomer ?? true

      const currentYampiAccumulate = validData.yampiAccumulate
      const originalYampiAccumulate = campaign.platformConfig?.yampi?.accumulate ?? false

      const currentYampiIgnore = validData.yampiIgnorePromotionProducts
      const originalYampiIgnore = campaign.platformConfig?.yampi?.ignorePromotionProducts ?? false

      const needsSync =
        validData.rewardType !== campaign.rewardType ||
        validData.rewardValue !== campaign.rewardValue ||
        currentGuruCycles != originalGuruCycles || // loose check for undefined/null/empty
        currentYampiOnce !== originalYampiOnce ||
        currentYampiAccumulate !== originalYampiAccumulate ||
        currentYampiIgnore !== originalYampiIgnore

      if (needsSync) {
        setPendingData(validData)
        setShowSyncAlert(true)
        return
      }
    }

    executeMutation(validData)
  }

  const isPending = isCreating || isUpdating

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Editar Campanha' : 'Nova Campanha'}</SheetTitle>
          <SheetDescription>
            Configure as regras de recompensa e integração desta campanha.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-4">
              <CampaignBasicInfo />
              <Separator />
              <CampaignRewards />
              <Separator />
              <CampaignIntegrations />
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={isPending}>
                {isEditing ? 'Salvar Alterações' : 'Criar Campanha'}
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>

      <AlertDialog open={showSyncAlert} onOpenChange={setShowSyncAlert}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertTriangle className="h-6 w-6 text-amber-600 sm:h-5 sm:w-5" />
              </div>
              <AlertDialogTitle>Atenção: Sincronização Necessária</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-4 pt-2">
              <p>
                Você alterou regras críticas (Valores/Tipos/Configurações). Para garantir a
                integridade, o sistema precisará atualizar todos os cupons já emitidos.
              </p>

              <div className="rounded-lg border bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold mb-2 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Impacto Operacional
                </div>
                <ul className="list-disc pl-4 space-y-1 opacity-90">
                  <li>Atualização em massa em segundo plano</li>
                  <li>Pode levar alguns minutos para propagar</li>
                  <li>Risco de vendas com regra antiga durante o processo</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingData(null)}>
              Voltar e Revisar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-600"
              onClick={() => {
                if (pendingData) executeMutation(pendingData)
              }}
            >
              Confirmar e Sincronizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  )
}
